import {
  Box,
  Button,
  HStack,
  ScaleFade,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  VStack,
  Flex,
  Switch,
  Center,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { CQReactTable, TextInput } from '@laxmimanogna/code-quick-components';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppColors from '../../../constants/AppColors';
import { FONT_FAMILY } from '../../../constants/Theme';
import batchDashboardRepository from '../../../repositories/BatchRepository';
import InfoTooltip from '../../../components/InfoTooltip';
import usePaginatedTable from '../../../hooks/usePaginatedTable';
import UploadBreakdownTable from '../components/UploadBreakdownTable';

const UploadTable = ({ filterParams }) => {
  const [searchText, setSearchText] = useState('');
  const tableRef = React.useRef(null);

  const getChartAccuracyData = useCallback(
    async (params) => {
      let updatedParams = { ...filterParams, ...params };
      if (filterParams?.label) {
        updatedParams['filter'] = filterParams.label.toLowerCase();

        if (filterParams.label.toLowerCase() !== 'custom') {
          delete updatedParams.start_date;
          delete updatedParams.end_date;
          delete updatedParams.previous_start_date;
          delete updatedParams.previous_end_date;
        }
      }
      if (updatedParams.health_system_id) {
        updatedParams.entity_type = 'health_system';
        updatedParams.entity_id = updatedParams.health_system_id;
      }
      if (updatedParams.hospital_id) {
        updatedParams.entity_type = 'hospital';
        updatedParams.entity_id = updatedParams.hospital_id;
      }
      if (updatedParams.department_id) {
        updatedParams.entity_type = 'department';
        updatedParams.entity_id = updatedParams.department_id;
      }
      if (updatedParams.provider_id) {
        updatedParams.entity_type = 'provider';
        updatedParams.entity_id = updatedParams.provider_id;
      }

      const hasId =
      updatedParams.health_system_id ||
      updatedParams.hospital_id ||
      updatedParams.department_id ||
      updatedParams.provider_id;

    if (!hasId) {
      return Promise.resolve(null); // or return [] based on your table
    }

      if (updatedParams.search === undefined) delete updatedParams.search;
      if (updatedParams.ordering === undefined) delete updatedParams.ordering;

      return await batchDashboardRepository.getChartAccuracyData(updatedParams);
    },
    [filterParams]
  );

  useEffect(() => {
    if (
      filterParams?.health_system_id ||
      filterParams?.hospital_id ||
      filterParams?.department_id ||
      filterParams?.provider_id
    ) {
      getChartAccuracyData({});
    }
  }, [
    filterParams?.health_system_id,
    filterParams?.hospital_id,
    filterParams?.department_id,
    filterParams?.provider_id,
    getChartAccuracyData
  ]);

  const {
    data: chartAccuracyData,
    isLoading: isChartAccuracyLoading,
    error,
    pagination,
    onPageChangeByNumber,
    onSizeChange,
    onPreviousPage,
    onNextPage,
    setSort,
    setSearchQuery,
    refetch,
    setAdditionalFilters,
  } = usePaginatedTable(getChartAccuracyData);

  const handleSearch = () => {
    setSearchQuery(searchText);
  };

  const renderContent = () => {
    return (
      <ScaleFade initialScale={0.5} in={true}>
        <UploadBreakdownTable
          data={chartAccuracyData}
          isLoading={isChartAccuracyLoading}
          pagination={pagination}
          onPageChangeByNumber={onPageChangeByNumber}
          onSizeChange={onSizeChange}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onSort={setSort}
          refetch={refetch}
          tableRef={tableRef}
        />
      </ScaleFade>
    );
  };

  const renderLoader = () => {
    return (
      <Stack p={4}>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  };

  const renderFailedPage = () => {
    return (
      <VStack justifyContent={'center'} h={'100%'} textAlign={'center'}>
        <Text>Something went wrong, Please try again later</Text>
      </VStack>
    );
  };

  const renderMainContent = () => {
    if (isChartAccuracyLoading && !chartAccuracyData?.length) {
      return renderLoader();
    } else if (error) {
      return renderFailedPage();
    } else {
      return renderContent();
    }
  };

  return (
    <Box
      marginTop={3}
      width={'100%'}
      bg={AppColors.white}
      padding={4}
      borderRadius={'lg'}
      shadow={'md'}
    >
      <HStack width={'100%'} justifyContent={'space-between'} mb={3}>
        <HStack>
          <Text fontFamily={FONT_FAMILY.ptSans} color={AppColors.primary}>
            Upload Breakdown
          </Text>
          <InfoTooltip
            infoContent={
              <>
                <p>
                  <b>Upload Breakdown</b> details each batch of charts uploaded,
                  sortable by each column header. It can also function as a work
                  queue by using the checkboxes in the far-right column,
                  allowing you to track and manage chart uploads effectively.
                </p>
              </>
            }
          />
        </HStack>
        <Box>
          <TextInput
            borderRadius={'md'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => {
              if (e.keyCode === 13) {
                handleSearch();
              }
            }}
            mb="2"
            rightIconProps={{ zIndex: 0 }}
            placeholder="Search"
            rightIcon={
              <SearchIcon
                onClick={handleSearch}
                color={AppColors.gray}
                cursor="pointer"
              />
            }
          />
        </Box>
      </HStack>
      {renderMainContent()}
    </Box>
  );
};
export default UploadTable;
